@if (gridView) {
  @if (scrollDisabled) {
    <ng-container *ngTemplateOutlet="contentTemplate"></ng-container>
  } @else {
    <div #scrollContainer class="max-h-full max-w-full h-full w-full overflow-auto">
      <ng-container *ngTemplateOutlet="contentTemplate"></ng-container>
    </div>
  }
}

<ng-template #contentTemplate>
  @if (useVirtualScroll) {
    <virtual-scroller
      #scroller
      [parentScroll]="parentScroll"
      [items]="displayedRows"
      [enableUnequalChildrenSizes]="true"
    >
      <table class="table-grid">
        <thead #header>
          <ng-container *ngTemplateOutlet="theadTemplate"></ng-container>
        </thead>

        <tbody #container>
          <ng-container
            *ngTemplateOutlet="tbodyTemplate; context: { scroller: scroller, rows: scroller.viewPortItems }"
          ></ng-container>
        </tbody>
      </table>
    </virtual-scroller>
  } @else {
    <table class="table-grid">
      <thead>
        <ng-container *ngTemplateOutlet="theadTemplate"></ng-container>
      </thead>

      <tbody>
        <ng-container *ngTemplateOutlet="tbodyTemplate; context: { rows: displayedRows }"></ng-container>
      </tbody>
    </table>
  }
</ng-template>

<ng-template #theadTemplate>
  <tr>
    @if (isMultiselectEnabled) {
      <th class="sticky w-8 z-10">
        <div class="header-value first-column {{ headerClass ? headerClass : '' }}">
          <app-multiselect-group-checkmark></app-multiselect-group-checkmark>
          <span class="sticky-seperator inline-block h-7 border-navy-50 border-r pl-5 w-1"></span>
        </div>
      </th>
    }

    @for (
      column of gridView.header;
      track headerCellIndex;
      let isFirst = $first;
      let isLast = $last;
      let headerCellIndex = $index
    ) {
      @if (!isColumnHidden(column)) {
        <th
          [style.width.px]="fixedWidth(column)"
          [style.right.px]="isColumnFixed(column) ? getColumnFixedPosition(column) : null"
          [id]="createHeaderCellId(headerCellIndex)"
          [attr.data-test-id]="'header-cell-' + headerCellIndex"
          (click)="columnHeaderClick(column)"
        >
          <div
            [class.first-column]="isFirst && !isMultiselectEnabled"
            [class.last-column]="isLast"
            [class.cursor-default]="!sortEnabled"
            class="header-value {{ column === sortedBy ? currentSortDirection : '' }} {{ headerClass || '' }}"
          >
            <span
              class="sticky-seperator inline-block h-7 border-navy-50 border-l pr-6 w-1"
              [class.hidden]="firstFixedHeader !== column"
            ></span>

            <ng-container
              *ngTemplateOutlet="
                headerCell;
                context: {
                  isFirst: isFirst && !isMultiselectEnabled,
                  isLast: isLast,
                  cellValue: column,
                }
              "
            ></ng-container>
          </div>
        </th>
      }
    }
  </tr>
</ng-template>

<ng-template #tbodyTemplate let-rows="rows" let-scroller="scroller">
  @for (row of rows; track rowTrackBy(rowIndex, row); let rowIndex = $index) {
    <tr
      [searchableRow]="row"
      [rowTrackBy]="searchableRowTrackBy"
      [attr.data-test-id]="'row-' + resolveOriginalId(row)"
      (searchFocusIn)="shouldShowMore(rowIndex)"
    >
      @if (isMultiselectEnabled) {
        <td
          [class.row-selected]="isRowSelected(row.rowId)"
          [class.bg-white]="useAlternateBackground && getRowIndexById(row.rowId) % 2 === 0"
          [class.bg-navy-10]="useAlternateBackground && getRowIndexById(row.rowId) % 2 !== 0"
        >
          <div class="row-value first-column gap-0" [ngClass]="{ 'pt-2 pb-1': useAlternateBackground }">
            <app-multiselect-checkmark
              class="align-self-start top-2 relative"
              [item]="row"
              (valueChange)="multiSelectValueChange()"
            ></app-multiselect-checkmark>
            <span class="sticky-seperator relative top-1 h-7 border-navy-50 border-r pl-5 w-1"></span>
          </div>
        </td>
      }

      @for (cell of row.cells; track cellIndex; let isFirst = $first; let isLast = $last; let cellIndex = $index) {
        @if (!isColumnHidden(gridView.header[cellIndex])) {
          <td
            [class.row-selected]="isMultiselectEnabled && isRowSelected(row.rowId)"
            [style.width.px]="fixedWidth(gridView.header[cellIndex])"
            [style.min-width.px]="fixedWidth(gridView.header[cellIndex])"
            [style.max-width.px]="fixeMaxdWidth(gridView.header[cellIndex])"
            [id]="createCellId(rowIndex, cellIndex)"
            [class.bg-white]="useAlternateBackground && getRowIndexById(row.rowId) % 2 === 0"
            [class.bg-navy-10]="useAlternateBackground && getRowIndexById(row.rowId) % 2 !== 0"
            [class.sticky]="isColumnFixed(gridView.header[cellIndex])"
            [style.right.px]="
              isColumnFixed(gridView.header[cellIndex]) ? getColumnFixedPosition(gridView.header[cellIndex]) : null
            "
          >
            <div
              class="row-value justify-center"
              [ngClass]="{ 'pt-2 pb-1': useAlternateBackground }"
              [class.first-column]="isFirst && !isMultiselectEnabled"
              [class.last-column]="isLast"
            >
              <span
                class="sticky-seperator relative top-1 h-7 border-navy-50 border-l pr-6 w-1 flex-1"
                [class.hidden]="firstFixedHeader !== gridView.header[cellIndex]"
              ></span>

              <ng-container
                *ngTemplateOutlet="
                  rowCell;
                  context: {
                    isFirst: isFirst && !isMultiselectEnabled,
                    isLast,
                    cellValue: cell,
                    stringCellValue: row.simplifiedCellsObject[gridView.header[cellIndex]],
                    objCellValue: row.cellsObject[gridView.header[cellIndex]]?.[0],
                    rowIndex,
                    cellHeader: gridView.header[cellIndex],
                    rowId: row.rowId,
                    originalRowId: row.originalrowIdentifier,
                    expandingData,
                    cellIndex,
                  }
                "
              ></ng-container>
            </div>
          </td>
        }
      }
    </tr>
  }
</ng-template>

<ng-template #headerCell let-isFirst="isFirst" let-isLast="isLast" let-cellValue="cellValue">
  <ng-container
    *ngTemplateOutlet="
      headerTemplate || defaultHeaderCell;
      context: {
        isFirst: isFirst,
        isLast: isLast,
        sorted: cellValue === sortedBy,
        sortDirection: currentSortDirection,
        cellValue: cellValue,
      }
    "
  ></ng-container>
</ng-template>

<ng-template
  #rowCell
  let-cellValue="cellValue"
  let-isFirst="isFirst"
  let-isLast="isLast"
  let-stringCellValue="stringCellValue"
  let-objCellValue="objCellValue"
  let-rowIndex="rowIndex"
  let-cellHeader="cellHeader"
  let-rowId="rowId"
  let-originalRowId="originalRowId"
  let-expandingData="expandingData"
  let-cellIndex="cellIndex"
>
  <ng-container
    *ngTemplateOutlet="
      cellTemplate || defaultRowCell;
      context: {
        isFirst: isFirst,
        isLast: isLast,
        cellValue: cellValue,
        stringCellValue: stringCellValue,
        objCellValue: objCellValue,
        rowIndex: rowIndex,
        cellHeader: cellHeader,
        rowId: rowId,
        originalRowId: originalRowId,
        expandingData: expandingData,
        cellIndex: cellIndex,
      }
    "
  ></ng-container>
</ng-template>

<ng-template #defaultHeaderCell let-isFirst="isFirst" let-isLast="isLast" let-cellValue="cellValue">
  <span>{{ cellValue }}</span>
</ng-template>
<ng-template #defaultRowCell let-isFirst="isFirst" let-isLast="isLast" let-cellValue="cellValue">
  <span>{{ cellValue }}</span>
</ng-template>
